import ApiRoutes from '@modules/account/defs/api-routes';
import { OfficeInfo, UpdateOfficeInfoInput } from '@modules/account/defs/types';
import useApi, { FetchApiOptions } from '@common/hooks/useApi';
import useSWR from 'swr';
import { useCallback } from 'react';
import { useSnackbar } from 'notistack';

interface UseOfficeReturn {
  data: OfficeInfo | null;
  isLoading: boolean;
  error: Error | null;
  mutate: () => void;
  updateOfficeInfo: (
    officeInfo: UpdateOfficeInfoInput
  ) => Promise<{ success: boolean; data?: OfficeInfo; errors?: string[] }>;
}

const useOffice = (): UseOfficeReturn => {
  const fetchApi = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const { data, error, mutate } = useSWR<OfficeInfo | null>(
    ApiRoutes.GetOfficeInfo,
    async (url: string): Promise<OfficeInfo | null> => {
      const response = await fetchApi<OfficeInfo>(url, {
        method: 'GET',
      });

      if (response.success && response.data) {
        return response.data;
      }

      return null;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const updateOfficeInfo = useCallback(
    async (officeInfo: UpdateOfficeInfoInput, options?: FetchApiOptions) => {
      const response = await fetchApi<OfficeInfo>(ApiRoutes.UpdateOfficeInfo, {
        method: 'PUT',
        data: officeInfo,
        ...options,
      });

      if (response.success && response.data) {
        await mutate();
        if (response.message) {
          enqueueSnackbar(response.message, { variant: 'success' });
        }
        return {
          success: response.success,
          data: response.data,
          errors: response.errors,
        };
      }

      return {
        success: response.success,
        errors: response.errors,
      };
    },
    [fetchApi, mutate, enqueueSnackbar]
  );

  return {
    data: data ?? null,
    isLoading: !error && data === undefined,
    error,
    mutate,
    updateOfficeInfo,
  };
};

export default useOffice;
